import { Button, Notification, Spinner, toast } from 'components/ui'
import React, { useContext, useEffect, useState } from 'react'
import ParticleBackground from 'react-particle-backgrounds'
import { particleBackgroundSettingsButton } from 'configs/particle-background.config';
import { BsStars } from 'react-icons/bs';
import { TbBuildingSkyscraper, TbPalette, TbDeviceAnalytics } from 'react-icons/tb';
import CardDialog from 'views/subscription/CardDialog';
import { api as Api } from 'api/http';
import { formatNumber } from 'utils/formatNumber';
import { HiRocketLaunch, HiOutlineShieldCheck } from 'react-icons/hi2';
import { Trans } from 'react-i18next';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { SessionContext } from 'contexts/SessionContext';
import { ModalContext } from 'contexts/ModalContext'



export default function WhitelabelModal() {
  
  const stripe = useStripe();
  const elements = useElements();

  const { user, updateUserData } = useContext(SessionContext);
  const { closeModal } = useContext(ModalContext);

  const [ cardOpened, setCardOpened ] = useState(false);
  const [ isSubmitting, setIsSubmitting ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(true);
  const [ billingFrequency, setBillingFrequency ] = useState(user?.subscription?.items?.[0]?.price?.recurring || 'month')
  const [ item, setItem ] = useState();
  const [ subscriptionCreated, setSubscriptionCreated ] = useState(false);

  const onSubmit = async (event, withPaymentMethod = true) => {
    event && event?.preventDefault();
    setIsSubmitting(true);

    let paymentMethod;
    if (withPaymentMethod) {
      const {error, paymentMethod: _paymentMethod} = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });
      if (error) {
        setIsSubmitting(false);
        toast.push(<Notification title={error.message} type="warning" />, { placement: 'top-center' });
        return;
      }
      paymentMethod = _paymentMethod;
    }

    let newProductPriceId = item?.prices?.filter(e => e.recurring == billingFrequency)?.[0]?.id;
    let newSubscription = user?.subscription?.items?.map((item) => {
      return {
        "id": item.price.id,
        "quantity": item.quantity
      }
    });

    if (newSubscription?.filter(item => item.id == newProductPriceId)?.length == 0) {
      newSubscription.push({
        "id": newProductPriceId,
        "quantity": 1
      });
    } else {
      newSubscription = newSubscription.map((item) => {
        if (item.id == newProductPriceId) {
          item.quantity = 1;
        }
        return item;
      })
    }
    
    setIsSubmitting(true);
    Api.put(`/subscriptions`, {}, { prices: newSubscription, payment_method_id: paymentMethod?.id || undefined, recurring: billingFrequency }).then((response) => {
      updateUserData();
      setIsSubmitting(false);
      closeModal();
      toast.push(<Notification title="Your White Label subscription has been successfully activated!" type="success" />, { placement: 'top-center' });
    }).catch((err) => {
      toast.push(<Notification title="An error ocurred. Please, try again later." type="danger" />, { placement: "top-center" });
    }).finally(() => {
      setIsSubmitting(false);
    });
  }

  const handleOnClick = () => {
    if (!!user?.subscription?.id && !!user?.pm_last_four) {
      onSubmit(undefined, false);
    } else {
      setCardOpened(true);
    }
  }

  const handleOnCloseCardDialog = () => {
    setCardOpened(false);
    if (subscriptionCreated) {
      closeModal();
    }
  }
  
  useEffect(() => {
    setIsLoading(true);
    Api.get(`/products?include=prices&filter[name]=white-label`).then(({ data: products }) => {
      setItem(products?.[0]);
    }).finally(() => {
      setIsLoading(false);
    });
  }, []);

  return (<>
    {!cardOpened
      ? <div className='relative'>
        <div className='bg-gradient-primary rounded-2xl overflow-hidden relative max-h-[90svh] overflow-y-auto'>
          <ParticleBackground settings={particleBackgroundSettingsButton} className="absolute pointer-events-none" />
          <div className='w-full h-full flex flex-col gap-8 lg:gap-10 items-center justify-center p-4 lg:p-8 py-8 lg:py-12 relative z-[1]'>
            <div className="flex items-center gap-3">
              <h2 className='mega-title text-white flex items-end gap-2'>
                <img 
                  src="/img/logo/logo-dark-full.png" 
                  className='h-8 mb-[2px]'
                />
                <span className='text-yellow-400 font-bold'>White Label</span>
              </h2>
              <BsStars className='text-yellow-400 text-2xl animate-pulse' />
            </div>
            
            <h3 className='text-white text-center text-lg lg:text-2xl font-black'>
              Your own AI platform with your brand
            </h3>
            <p className='text-tertiary-300 text-center text-sm -mt-4 lg:-mt-6 font-bold'>
              Transform your agency with our complete White Label solution. Full brand control, secure infrastructure, and dedicated support.
            </p>
            
            <div className='grid lg:grid-cols-2 gap-4'>
              <div className='flex items-center gap-2.5 px-4 relative'>
                <div className='relative'>
                  <span className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] rounded-full' />
                  <TbBuildingSkyscraper 
                    className='text-3xl text-yellow-400 relative z-[1]'
                  />
                </div>
                <span className='text-white text-xs font-bold'>Custom domain with your brand</span>
              </div>
              <div className='flex items-center gap-2.5 px-4 relative'>
                <div className='relative'>
                  <span className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] rounded-full' />
                  <TbPalette 
                    className='text-3xl text-yellow-400 relative z-[1]'
                  />
                </div>
                <span className='text-white text-xs font-bold'>Complete customization of colors and logos</span>
              </div>
              <div className='flex items-center gap-2.5 px-4 relative'>
                <div className='relative'>
                  <span className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] rounded-full' />
                  <HiOutlineShieldCheck 
                    className='text-3xl text-yellow-400 relative z-[1]'
                  />
                </div>
                <span className='text-white text-xs font-bold'>Enterprise-grade security</span>
              </div>
              <div className='flex items-center gap-2.5 px-4 relative'>
                <div className='relative'>
                  <span className='block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] rounded-full' />
                  <TbDeviceAnalytics 
                    className='text-3xl text-yellow-400 relative z-[1]'
                  />
                </div>
                <span className='text-white text-xs font-bold'>Advanced analytics and custom reports</span>
              </div>
            </div>

            <div className='flex flex-col gap-0 items-center mt-4'>
              <p className='text-white font-semibold text-sm'>Special price</p>
              <h1 className='mega-title !text-5xl group-[.is-active]:!text-white text-right text-white'>
                {isLoading
                  ? <Spinner size="48" />
                  : <>${formatNumber((item?.prices?.filter(e => e.recurring == billingFrequency)[0])?.amount / 100, 2)}</>
                }
              </h1>
              {!user?.subscription?.items?.[0]?.price?.recurring ? (
                <div className='flex gap-2 mt-4'>
                  <div className={`transition-all cursor-pointer flex items-center justify-between text-white gap-2 ${billingFrequency == `month` && `p-1 px-2.5 bg-secondary-500 font-semibold !text-tertiary-900 rounded-full`}`} >
                    <span onClick={() => setBillingFrequency(`month`)}>
                      <Trans i18nKey={`subscription.monthly`}>Monthly</Trans>
                    </span>
                  </div>
                  <div className={`transition-all cursor-pointer flex items-center justify-between text-white gap-2 ${billingFrequency == `year` && `p-1 px-2.5 bg-secondary-500 font-semibold !text-tertiary-900 rounded-full`}`} >
                    <span className='flex items-center gap-2 w-full' onClick={() => setBillingFrequency(`year`)}>
                      <span><Trans i18nKey={`subscription.annualy`}>Annually</Trans></span> 
                      <Button variant="ai" className='-mr-1 font-bold rounded-lg !p-1 !px-2.5 inline-block !h-auto !text-xs flex gap-1.5 items-center bg-primary w-auto'>
                        <HiRocketLaunch />
                        <span>-35%</span>
                      </Button>
                    </span>
                  </div>
                </div>
              ) : user?.subscription?.items?.[0]?.price?.recurring == 'month' ? (
                <div className={`mt-4 transition-all cursor-pointer flex items-center justify-between text-white gap-2 ${billingFrequency == `month` && `p-1 px-2.5 bg-secondary-500 font-semibold !text-tertiary-900 rounded-full`}`} >
                  <span onClick={() => setBillingFrequency(`month`)}>
                    <Trans i18nKey={`subscription.monthly`}>Monthly</Trans>
                  </span>
                </div>
              ) : (
                <div className={`mt-4 transition-all cursor-pointer flex items-center justify-between text-white gap-2 ${billingFrequency == `year` && `p-1 px-2.5 bg-secondary-500 font-semibold !text-tertiary-900 rounded-full`}`} >
                  <span className='flex items-center gap-2 w-full' onClick={() => setBillingFrequency(`year`)}>
                    <span><Trans i18nKey={`subscription.annualy`}>Annually</Trans></span> 
                    <Button variant="ai" className='-mr-1 font-bold rounded-lg !p-1 !px-2.5 inline-block !h-auto !text-xs flex gap-1.5 items-center bg-primary w-auto'>
                      <HiRocketLaunch />
                      <span>-35%</span>
                    </Button>
                  </span>
                </div>
              )}
            </div>

            <div className='flex justify-center gap-2 mt-4'>
              <Button 
                variant="ai"
                icon={<BsStars />}
                onClick={handleOnClick}
                className="border-2 !bg-yellow-500 hover:!bg-yellow-600 dark:!bg-yellow-500/90 hover:dark:!bg-yellow-600/90 !text-white border-white/20 hover:border-white/40 hover:scale-105 transition-all"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                Purchase Now
              </Button>
            </div>
          </div>
        </div>
      </div>
      : <CardDialog 
        isOnlyContent={false}
        cardDialogOpened={cardOpened}
        setCardDialogOpened={() => handleOnCloseCardDialog()}
        handleSubmitStripe={onSubmit}
        isSubmittingCard={isSubmitting}
        subscriptionCreated={subscriptionCreated}
        isWhitelabel={true}
      />
    }
  </>)
} 